/**
 * Market identity and country-level presentation defaults.
 *
 * Keep these independent from host ownership and rollout state, which live in
 * `domains.ts`. Tenant configuration can use a market profile without implying
 * that the market's public domain is active.
 */

export const MARKET_IDS = ["nz", "au", "us"] as const;
export type MarketId = (typeof MARKET_IDS)[number];

export type MarketProfile = {
  countryCode: string;
  locale: string;
  currency: string;
};

export const MARKET_PROFILES = {
  nz: {
    countryCode: "NZ",
    locale: "en-NZ",
    currency: "NZD",
  },
  au: {
    countryCode: "AU",
    locale: "en-AU",
    currency: "AUD",
  },
  us: {
    countryCode: "US",
    locale: "en-US",
    currency: "USD",
  },
} as const satisfies Record<MarketId, MarketProfile>;

export const DEFAULT_MARKET_ID: MarketId = "nz";

/** Maps a tenant's ISO country code to a supported Strelve market. */
export function getMarketIdForCountryCode(
  countryCode: string | null | undefined
): MarketId | null {
  const normalized = countryCode?.trim().toUpperCase();
  if (!normalized) return null;
  return (
    MARKET_IDS.find(
      (marketId) => MARKET_PROFILES[marketId].countryCode === normalized
    ) ?? null
  );
}
